import { useState } from 'react'

import { useQueries } from '@tanstack/react-query'
import { RowClickedEvent } from 'ag-grid-enterprise'
import { NumericFormat } from 'react-number-format'

import { getTMinus1, toISO8601 } from '@fortem-monorepo/js-utilities'
import { numberFormatter } from '@fortem-monorepo/js-utilities'
import { createColDefs } from '@fortem-monorepo/react-ag'
import { CustomAgGrid } from '@fortem-monorepo/react-ag'
import { FiltersProvider, useApi, useFilters } from '@fortem-monorepo/react-spa'
import { createFilters } from '@fortem-monorepo/react-spa'
import { HeaderConfigBar } from '@fortem-monorepo/react-spa'
import { Widget, WidgetContent, WidgetHeaderWithQuery } from '@fortem-monorepo/react-spa'
import { DatePicker } from '@fortem-monorepo/react-ui'
import { SearchSelect } from '@fortem-monorepo/react-ui'
import { FormItem } from '@fortem-monorepo/react-ui'
import { Input } from '@fortem-monorepo/react-ui'
import { Label } from '@fortem-monorepo/react-ui'
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@fortem-monorepo/react-ui'
import { BondsRec } from '@fortem-monorepo/web-sdk'

import { Page } from '@/components/Page'

const filters = createFilters([
  {
    name: 'value_date',
    type: 'date',
    label: 'Value Date',
    defaultValue: getTMinus1(),
    params: true,
  },
  {
    name: 'tolerance',
    type: 'number',
    label: 'Tolerance',
    defaultValue: 0.001,
    debounce: 600,
    params: true,
  },
  {
    name: 'subset',
    type: 'select',
    label: 'Subset',
    defaultValue: 'All',
    params: true,
  },
])

const subsetOptions = [
  { label: 'Show All', value: 'All' },
  { label: 'Custodian vs Dashboard', value: 'Custodian vs Dashboard' },
  { label: 'Blotter vs Dashboard', value: 'Blotter vs Dashboard' },
  { label: 'Custodian vs Blotter', value: 'Custodian vs Blotter' },
]

const notionaldiscrepanciesColDefs = createColDefs([
  { field: 'category', rowGroup: true, hide: true, width: 400 },
  { field: 'fund_name', headerName: 'Fund', width: 100 },
  { field: 'isin', width: 115 },

  {
    field: 'notional_x',
    width: 125,
    valueFormatter: numberFormatter({ delimiter: true }),
  },
  {
    field: 'notional_y',
    width: 125,
    valueFormatter: numberFormatter({ delimiter: true }),
  },
  {
    field: 'diff',
    width: 125,
    valueFormatter: numberFormatter({ delimiter: true }),
  },
])

const missingEntriesColDefs = createColDefs([
  { field: 'category', rowGroup: true, hide: true, width: 400 },
  { field: 'fund_name', headerName: 'Fund', width: 100 },
  { field: 'isin', width: 115 },
  {
    field: 'notional_x',
    width: 125,
    valueFormatter: numberFormatter({ delimiter: true }),
  },
  {
    field: 'notional_y',
    width: 125,
    valueFormatter: numberFormatter({ delimiter: true }),
  },
])

const missingYieldLockColDefs = createColDefs([
  {
    field: 'blotterid',
    width: 200,
  },
  {
    field: 'productname',
    width: 200,
  },
  {
    field: 'fundname',
    width: 200,
    rowGroup: true,
    hide: true,
  },
  {
    field: 'notional',
    width: 200,
    valueFormatter: numberFormatter({ delimiter: true }),
  },
  {
    field: 'price',
    width: 200,
  },
  {
    field: 'tradedate',
    width: 200,
  },
  {
    field: 'yieldlock',
    width: 200,
  },
])

const isinDataColDefs = createColDefs([
  { field: 'table', headerName: 'Table', width: 180 },
  { field: 'has_data', headerName: 'Data', width: 100 },
])

export interface BondsDataRecPageProps {}

export const BondsDataRecPage: React.FC<BondsDataRecPageProps> = ({}) => {
  return (
    <Page metadata={{ title: 'Bonds Data Rec' }}>
      <FiltersProvider filters={filters}>
        <BondsDataRecContents />
      </FiltersProvider>
    </Page>
  )
}

export interface BondsDataRecContentsProps {}

export const BondsDataRecContents: React.FC<BondsDataRecContentsProps> = ({}) => {
  const { values, registerFilter } = useFilters<typeof filters>()
  const [fund_name, _setFundName] = useState<string | null>(null)
  const [isin, _setISIN] = useState<string | null>(null)

  const isinDataParams = {
    fund_name: fund_name!,
    isin: isin!,
    value_date: toISO8601(values.value_date ?? getTMinus1()),
  }

  const missingEntriesParams = {
    subset: values.subset!,
    tolerance: values.tolerance!,
    value_date: toISO8601(values.value_date ?? getTMinus1()),
  }

  const api = useApi()

  const queries = useQueries({
    queries: [
      {
        queryKey: ['bonds-data-rec', 'isin-data', isinDataParams],
        queryFn: () => api.bondsRec.getISINData(isinDataParams),
        enabled: !!fund_name && !!isin && !!values.value_date,
      },
      {
        queryKey: ['bonds-data-rec', 'missing-entries', missingEntriesParams],
        queryFn: () => api.bondsRec.getMissingEntries(missingEntriesParams),
        enabled: !!values.value_date,
      },
      {
        queryKey: ['bonds-data-rec', 'notional-discrepancies', missingEntriesParams],
        queryFn: () => api.bondsRec.getNotionaldiscrepancies(missingEntriesParams),
        enabled: !!values.value_date,
      },
      {
        queryKey: ['bonds-data-rec', 'missing-yield-lock'],
        queryFn: () => api.bondsRec.getMissingYieldLock(),
        refetchInterval: 60_000, // 1min
      },
    ],
  })

  const [isinData, missingEntries, notionaldiscrepancies, missingYieldLock] = queries

  return (
    <>
      <HeaderConfigBar>
        <div className="flex items-center gap-2">
          <FormItem>
            <Label>Value Date</Label>
            <DatePicker {...registerFilter('value_date')} size="sm" />
          </FormItem>
          <FormItem>
            <Label>Tolerance</Label>
            <NumericFormat
              {...registerFilter('tolerance')}
              customInput={Input}
              inputSize={'sm'}
              thousandSeparator={true}
            />
          </FormItem>
          <FormItem>
            <Label>Subset</Label>
            <SearchSelect {...registerFilter('subset')} size="sm" options={subsetOptions} />
          </FormItem>
        </div>
      </HeaderConfigBar>
      <ResizablePanelGroup direction={'vertical'} className={'flex-1'}>
        <ResizablePanel>
          <ResizablePanelGroup direction={'horizontal'}>
            <ResizablePanel minSize={14} maxSize={14}>
              <Widget exposed className={'h-full'}>
                <WidgetHeaderWithQuery title="ISIN Data" query={isinData} />
                <WidgetContent loading={isinData.isLoading}>
                  <CustomAgGrid
                    domLayout={'normal'}
                    query={isinData}
                    rowData={isinData.data ?? []}
                    columnDefs={isinDataColDefs}
                  />
                </WidgetContent>
              </Widget>
            </ResizablePanel>
            <ResizableHandle />
            <ResizablePanel defaultSize={43}>
              <Widget exposed className={'h-full'}>
                <WidgetHeaderWithQuery title="Missing Entries" query={missingEntries} />
                <WidgetContent loading={missingEntries.isLoading}>
                  <CustomAgGrid
                    domLayout={'normal'}
                    query={missingEntries}
                    onRowClicked={(
                      row: RowClickedEvent<BondsRec.GetMissingEntriesData[number]>,
                    ) => {
                      if (row.node.group) return
                      const fund_name = row.data?.fund_name!
                      const isin = row.data?.isin!
                      _setFundName(fund_name)
                      _setISIN(isin!)
                    }}
                    rowData={missingEntries.data}
                    columnDefs={missingEntriesColDefs}
                  />
                </WidgetContent>
              </Widget>
            </ResizablePanel>
            <ResizableHandle />
            <ResizablePanel defaultSize={43}>
              <Widget exposed className={'h-full'}>
                <WidgetHeaderWithQuery
                  title="Notional discrepancies"
                  query={notionaldiscrepancies}
                />
                <WidgetContent loading={notionaldiscrepancies.isLoading}>
                  <CustomAgGrid
                    domLayout={'normal'}
                    query={notionaldiscrepancies}
                    onRowClicked={(
                      row: RowClickedEvent<BondsRec.GetNotionaldiscrepanciesData[number]>,
                    ) => {
                      if (row.node.group) return
                      const fund_name = row.data?.fund_name!
                      const isin = row.data?.isin!
                      _setFundName(fund_name)
                      _setISIN(isin!)
                    }}
                    rowData={notionaldiscrepancies.data}
                    columnDefs={notionaldiscrepanciesColDefs}
                  />
                </WidgetContent>
              </Widget>
            </ResizablePanel>
          </ResizablePanelGroup>
        </ResizablePanel>
        <ResizableHandle />
        <ResizablePanel>
          <Widget exposed className={'h-full'}>
            <WidgetHeaderWithQuery title="Missing Yield Lock" query={missingYieldLock} />
            <WidgetContent loading={missingYieldLock.isLoading}>
              <CustomAgGrid
                domLayout={'normal'}
                query={missingYieldLock}
                rowData={missingYieldLock.data}
                columnDefs={missingYieldLockColDefs}
              />
            </WidgetContent>
          </Widget>
        </ResizablePanel>
      </ResizablePanelGroup>
    </>
  )
}
